import { useLayoutEffect, useRef, useState } from 'react';
import { API } from '@/config/constants';
import { readPublicReceiptResource, validatePublicReceipt } from '@/utils/publicReceiptResource';

// One keyed page opening owns the receipt and all its independent media reads.
// No staff session or native runtime is an authority for a public link.
export function usePublicReceipt(orderId, token) {
  const [result, setResult] = useState({ state: token ? 'loading' : 'unavailable', media: {} });
  const retryRef = useRef(null);

  useLayoutEffect(() => {
    const controller = new AbortController();
    const urls = new Set();
    const media = {};
    let data;
    const current = () => !controller.signal.aborted;
    const publish = () => {
      if (current()) setResult({ state: 'available', data, media: { ...media } });
    };
    const loadMedium = async (key, descriptor) => {
      if (!current() || media[key]?.state === 'loading') return;
      media[key] = { ...descriptor, state: 'loading' };
      publish();
      try {
        const blob = await readPublicReceiptResource(descriptor.endpoint, token, { signal: controller.signal, media: true });
        if (!current()) return;
        const url = URL.createObjectURL(blob);
        urls.add(url);
        media[key] = { ...descriptor, state: 'available', url };
      } catch {
        if (!current()) return;
        media[key] = { ...descriptor, state: 'unavailable' };
      }
      publish();
    };
    retryRef.current = () => {
      if (!current()) return;
      Object.entries(media).forEach(([key, item]) => {
        if (item.state === 'unavailable') void loadMedium(key, item);
      });
    };

    if (token && API !== null) {
      const base = `${API}/pod/${encodeURIComponent(orderId)}`;
      readPublicReceiptResource(base, token, { signal: controller.signal }).then(value => {
        if (!current()) return;
        data = validatePublicReceipt(value, orderId);
        publish();
        data.confirmation.media_files.forEach((file, index) => {
          const label = file.file_type === 'signature' ? 'Signature'
            : file.file_type === 'video' ? `Video ${index + 1}` : `Delivery photo ${index + 1}`;
          void loadMedium(String(index), { label, endpoint: `${base}/media/${index}` });
        });
        if (data.has_arrangement_photo) void loadMedium('arrangement', { label: 'Arrangement photo', endpoint: `${base}/arrangement-photo` });
      }).catch(error => {
        if (current()) setResult({ state: 'unavailable', error: error.message, media: {} });
      });
    } else setResult({ state: 'unavailable', media: {} });

    return () => {
      controller.abort();
      retryRef.current = null;
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [orderId, token]);

  return { ...result, retryMedia: () => retryRef.current?.() };
}
